import { registry } from './registry.ts';

export const STUDENT_APP_TARGET = '/app/';

/**
 * 🛡️ PAYMENT GATE — resolves where to redirect a Google OAuth user.
 *
 * Rules:
 *  - profile.status = 'active' AND active subscription → '#/map' (returning paying student),
 *    or '/app/' in production when the role is student (no kids students left)
 *  - anything else (new user, pending, no sub) → '/app/' — this app has no enrollment
 *    flow of its own anymore; the student app's own billing lock decides what to show.
 *
 * Safe default: '/app/'. We never skip payment verification.
 */
export async function resolveOAuthRedirectTarget(userId: string): Promise<string> {
    if (!userId) return STUDENT_APP_TARGET;

    try {
        const supabase = registry.get<import('@supabase/supabase-js').SupabaseClient>('Supabase');
        if (!supabase) return STUDENT_APP_TARGET;

        const [{ data: profile }, { data: sub }] = await Promise.all([
            supabase.from('profiles').select('status, role').eq('id', userId).maybeSingle(),
            supabase
                .from('subscriptions')
                .select('status')
                .eq('user_id', userId)
                .in('status', ['active', 'trialing', 'pending_renewal'])
                .maybeSingle(),
        ]);

        if (profile?.status === 'active' && sub) {
            // Production has no kids students left: a student goes straight to /app/.
            if (profile.role === 'student') return STUDENT_APP_TARGET;
            console.log('✅ OAuth gate: returning active student → /map');
            return '#/map';
        }

        console.log('🎓 OAuth gate: new/pending user → /app/', {
            profileStatus: profile?.status ?? 'no profile',
            hasSub: !!sub,
        });
        return STUDENT_APP_TARGET;
    } catch (err) {
        console.warn('⚠️ OAuth gate check failed, defaulting to /app/:', err);
        return STUDENT_APP_TARGET;
    }
}
